/**
 * 인보이스 필드 단일 소스 (single source of truth).
 *
 * ── 정책 ──
 *
 * 1. **CSV 출력 (납품 형식) — 일본어 영구 고정**
 *    `csvHeader` 컬럼이 백엔드 ``csv_writer.py`` 의 헤더와 1:1 정확히 일치한다.
 *    납품 형식이 바뀌면 양쪽 모두 수정해야 한다.
 *
 * 2. **UI 표시 라벨 — i18n 으로 ko/ja 전환**
 *    `labelI18nKey` 가 가리키는 키를 ``t()`` 로 해석. 컴포넌트는 항상 i18n 으로
 *    라벨을 표시해야 한다. (단, CSV 헤더 / "確認用 컬럼" 같이 인보이스 양식 표준
 *    일본어를 의도적으로 노출하는 좁은 영역은 ``csvHeader`` 를 그대로 사용.)
 *
 * 3. **두 종류의 필드 정의**
 *    - ``EDITABLE_FIELDS`` (16) — record 편집 / CSV 노출 컬럼
 *    - ``LLM_FIELDS``       (12) — invoice_extractor.py 의 LLM 출력 필드.
 *      ``Agent.extraction_rules`` 의 키로 사용됨.
 *    두 set 는 일부 겹치지만 의도/맥락이 달라 별도 array 로 유지.
 *
 * 백엔드 ``EDITABLE_CSV_FIELDS`` 와 ``EDITABLE_FIELDS`` 는 1:1 매칭.
 * 키가 추가되면 양쪽 모두 수정.
 */

export type EditableFieldKey =
  | "billing_party"
  | "swift_code"
  | "agent_name"
  | "agent_invoice_number"
  | "invoice_date"
  | "total_amount"
  | "withholding_tax"
  | "ref_number"
  | "remarks"
  | "final_payable_amount"
  | "agent_id"
  | "agent_currency"
  | "is_ttdc_text"
  | "original_folder"
  | "local_agent_code"
  | "document_id";

export interface FieldMeta {
  key: EditableFieldKey;

  /** UI 표시용 i18n 키. 컴포넌트 안에서 ``t(labelI18nKey)`` 로 해석. */
  labelI18nKey: string;

  /**
   * CSV 출력 / 인보이스 양식 표준 일본어 컬럼명.
   * 백엔드 ``csv_writer.py`` 의 헤더와 정확히 일치 — 납품 형식 고정.
   * 다국어 처리하지 않는다.
   */
  csvHeader: string;

  /** placeholder/도움말 i18n 키 (선택). 컴포넌트 안에서 ``t(hintI18nKey)`` 로 해석. */
  hintI18nKey?: string;
  type?: "text" | "amount" | "date";
}

/** 화면 표시 순서 — 인보이스 표준 17 컬럼 흐름을 따른다. */
export const EDITABLE_FIELDS: readonly FieldMeta[] = [
  {
    key: "document_id",
    labelI18nKey: "invoice.fields.document_id.label",
    csvHeader: "PDF名",
    hintI18nKey: "records.fields.hint.document_id",
  },
  {
    key: "local_agent_code",
    labelI18nKey: "invoice.fields.local_agent_code.label",
    csvHeader: "現地代理人コード",
    hintI18nKey: "records.fields.hint.local_agent_code",
  },
  {
    key: "billing_party",
    labelI18nKey: "invoice.fields.billing_party.label",
    csvHeader: "請求先",
  },
  {
    key: "swift_code",
    labelI18nKey: "invoice.fields.swift_code.label",
    csvHeader: "SWIFTコード",
  },
  {
    key: "agent_name",
    labelI18nKey: "invoice.fields.agent_name.label",
    csvHeader: "代理人名",
  },
  {
    key: "agent_invoice_number",
    labelI18nKey: "invoice.fields.agent_invoice_number.label",
    csvHeader: "代理人インボイスNo",
  },
  {
    key: "invoice_date",
    labelI18nKey: "invoice.fields.invoice_date.label",
    csvHeader: "インボイス発行日",
    type: "date",
  },
  {
    key: "total_amount",
    labelI18nKey: "invoice.fields.total_amount.label",
    csvHeader: "請求金額（合計）",
    type: "amount",
  },
  {
    key: "withholding_tax",
    labelI18nKey: "invoice.fields.withholding_tax.label",
    csvHeader: "源泉所得税額",
    type: "amount",
  },
  {
    key: "ref_number",
    labelI18nKey: "invoice.fields.ref_number.label",
    csvHeader: "TTDC案件番号",
  },
  {
    key: "remarks",
    labelI18nKey: "invoice.fields.remarks.label",
    csvHeader: "備考",
  },
  {
    key: "final_payable_amount",
    labelI18nKey: "invoice.fields.final_payable_amount.label",
    csvHeader: "実際請求金額（確認用）",
    type: "amount",
  },
  {
    key: "agent_id",
    labelI18nKey: "invoice.fields.agent_id.label",
    csvHeader: "代理人ID（確認用）",
  },
  {
    key: "agent_currency",
    labelI18nKey: "invoice.fields.agent_currency.label",
    csvHeader: "通貨（確認用）",
  },
  {
    key: "is_ttdc_text",
    labelI18nKey: "invoice.fields.is_ttdc_text.label",
    csvHeader: "TTDC表記（確認用）",
  },
  {
    key: "original_folder",
    labelI18nKey: "invoice.fields.original_folder.label",
    csvHeader: "元フォルダ（確認用）",
  },
] as const;

/** key → meta 매핑 (조회용). */
export const FIELD_META_MAP: Readonly<Record<EditableFieldKey, FieldMeta>> = Object.fromEntries(
  EDITABLE_FIELDS.map((m) => [m.key, m]),
) as Record<EditableFieldKey, FieldMeta>;

/**
 * 일본어 라벨 (CSV 헤더) 조회.
 *
 * @deprecated UI 표시용으로는 ``t(meta.labelI18nKey)`` 를 사용할 것.
 * 본 함수는 CSV/인보이스 양식 표준 일본어를 의도적으로 노출해야 하는 좁은 영역
 * (예: 미가공 CSV diff 미리보기) 에서만 사용 가능. 일반 화면 라벨이라면
 * ``FIELD_META_MAP[key]?.labelI18nKey`` 를 ``t()`` 로 해석할 것.
 */
export function labelOf(key: string): string {
  return (FIELD_META_MAP as Record<string, FieldMeta>)[key]?.csvHeader ?? key;
}

// ────────────── LLM 출력 필드 (12) — extraction_rules editor 용 ──────────────

export interface LLMFieldMeta {
  /** invoice_extractor.py 의 출력 JSON 키. ``Agent.extraction_rules`` 의 키로도 사용. */
  key: string;
  labelI18nKey: string;
  /**
   * 참고용 일본어 (CSV 에 동일 필드가 있으면 그것과 일치).
   * 일부 LLM 전용 필드(예: ``*_visible``, ``is_ttdc``, ``swift_codes``) 는
   * CSV 에 없거나 표현이 다르므로 별도 일본어를 부여한다.
   */
  csvHeader: string;
}

export const LLM_FIELDS: readonly LLMFieldMeta[] = [
  {
    key: "billing_party",
    labelI18nKey: "invoice.fields.billing_party.label",
    csvHeader: "請求先",
  },
  {
    key: "agent_name",
    labelI18nKey: "invoice.fields.agent_name.label",
    csvHeader: "代理人名",
  },
  {
    key: "agent_invoice_number",
    labelI18nKey: "invoice.fields.agent_invoice_number.label",
    csvHeader: "代理人インボイスNo",
  },
  {
    key: "invoice_date",
    labelI18nKey: "invoice.fields.invoice_date.label",
    csvHeader: "インボイス発行日",
  },
  {
    key: "ref_number",
    labelI18nKey: "invoice.fields.ref_number.label",
    csvHeader: "TTDC案件番号",
  },
  {
    key: "total_amount",
    labelI18nKey: "invoice.fields.total_amount.label",
    csvHeader: "請求金額（合計）",
  },
  {
    key: "billed_total_visible",
    labelI18nKey: "invoice.fields.billed_total_visible.label",
    csvHeader: "請求総額（書面表示）",
  },
  {
    key: "payable_total_visible",
    labelI18nKey: "invoice.fields.payable_total_visible.label",
    csvHeader: "支払総額（書面表示）",
  },
  {
    key: "withholding_tax_visible",
    labelI18nKey: "invoice.fields.withholding_tax_visible.label",
    csvHeader: "源泉徴収税（書面表示）",
  },
  {
    key: "withholding_tax",
    labelI18nKey: "invoice.fields.withholding_tax.label",
    csvHeader: "源泉所得税額",
  },
  {
    key: "is_ttdc",
    labelI18nKey: "invoice.fields.is_ttdc.label",
    csvHeader: "TTDC請求対象",
  },
  {
    key: "swift_codes",
    labelI18nKey: "invoice.fields.swift_codes.label",
    csvHeader: "SWIFTコード一覧",
  },
] as const;

/** LLM 필드 key 목록 (string array — 기존 KNOWN_INVOICE_FIELDS 호환). */
export const LLM_FIELD_KEYS = LLM_FIELDS.map((f) => f.key);

/** LLM 필드 key → meta 매핑. */
export const LLM_FIELD_META_MAP: Readonly<Record<string, LLMFieldMeta>> = Object.fromEntries(
  LLM_FIELDS.map((m) => [m.key, m]),
);
